import { useDispatch, useSelector } from 'react-redux';
import { XIcon } from '../assets/X';

import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';
import { userInfo } from '../store/selectors/user';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from './ui/form';
import InputField from './form/InputFiled';

import { setUser } from '../store/slices/userSlice';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useUpdateUser } from '../modules/profile/services/update';
import { LoadingButton } from './ui/loadingButton';

type EmailDrawerProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const emailSchema = z.object({
  email: z.string().trim().email('validation.email'),
});
type EmailFormSchema = z.infer<typeof emailSchema>;

export const EmailDrawer = ({ open, setOpen }: EmailDrawerProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(userInfo);
  const { updateUser, isPending } = useUpdateUser();

  const form = useForm<EmailFormSchema>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: user.email || '',
    },
  });

  const email = form.watch('email') ?? '';

  const handleSubmit = async (data: EmailFormSchema) => {
    try {
      const res = await updateUser({
        email: data.email,
      });
      if (res.data.success) {
        dispatch(
          setUser({
            ...user,
            email: data.email,
          })
        );
        setOpen(false);
      }
    } catch {
      // ignore
    }
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent className="bg-fill-background rounded-t-[24px] flex flex-col ">
        <div className="flex flex-row justify-between items-center px-4">
          <DrawerTitle className="text-display-md">E-mail</DrawerTitle>
          <div
            className="flex items-center justify-center h-6 w-6 rounded-full bg-button-secondary"
            onClick={() => setOpen(false)}
          >
            <XIcon className="text-text-icon-secondary" />
          </div>
        </div>

        <Form {...form}>
          <form
            className={cn('flex flex-col space-y-5 pb-8 pt-4 w-full px-4', {})}
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <InputField
              name="email"
              type="email"
              placeholder="example@email.com"
              className="w-full h-16 border-none text-[22px] font-semibold rounded-[16px] leading-7 tracking-[0px] caret-button-primary px-4 py-[18px]"
            />
            <LoadingButton
              loading={isPending}
              disabled={!email.trim()}
              type="submit"
              className="w-full bg-button-primary h-12 text-[16px] font-semibold font-inter leading-5 tracking-[0px] text-text-white rounded-xl"
            >
              {t('save')}
            </LoadingButton>
          </form>
        </Form>
      </DrawerContent>
    </Drawer>
  );
};
